import React, { use, useEffect } from "react";
import { Link } from "react-router-dom";
import { loginUser } from "../../../features/userSlice";
import { useDispatch, useSelector } from "react-redux";

const LandingPage = () => {
  const dispatch = useDispatch();
  const [message, setMessage] = React.useState(null);
  const userdata = useSelector((state) => state.user); 
  const [inputValues, setInputValues] = React.useState({
    email: "",
    password: "",
  });

  const submitLogin = async (e) => {
    e.preventDefault();

    // Show checking...
    setMessage(
      <div className="border border-blue-600 px-4 py-1 rounded mb-4 text-blue-600 bg-blue-50">
        <p className="text-sm">Checking...</p>
      </div>
    );

    // Delay for validation
    setTimeout(() => {
      const errors = [];

      if (!inputValues.email.trim()) {
        errors.push("Email is required");
      }

      if (!inputValues.password.trim()) {
        errors.push("Password is required");
      }

      if (errors.length > 0) {
        setMessage(
          <div className="border border-red-600 px-4 py-2 rounded mb-4 text-red-600 bg-red-50">
            <ul className="list-disc pl-5 text-sm">
              {errors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>
        );
        return;
      }

      const data = dispatch(
        loginUser({ email: inputValues.email, password: inputValues.password })
      );
      data.then((res) => {
        if (res.type === "userAuth/loginUser/fulfilled") {
          setMessage(
            <div className="border border-green-600 px-4 py-1 rounded mb-4 text-green-600 bg-green-50">
              <p className="text-sm">Login successful! Redirecting...</p>
            </div>
          );
          // Redirect or further actions can be added here
          setTimeout(() => {
            window.location.href = "/"; // Replace with your desired redirect URL
          }, 2000);
        }
        if (res.type === "userAuth/loginUser/rejected") {
          setMessage(
            <div className="border border-red-600 px-4 py-1 rounded mb-4 text-red-600 bg-red-50">
              <p className="text-sm">
                {userdata.error || "Login failed. Please try again."}
              </p>
            </div>
          );
        }
      });

      // Debounce message reset;
    }, 1000);
  };

  return (
    // Main container ensuring min-height covers the full viewport (100vh)
    <div className="flex flex-col min-h-screen bg-gray-50 font-sans">
      {/* Header Section */}
      <header className="py-4 px-8 border-b border-gray-200 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto">
          {/* Logo Placeholder */}
          <div className="text-2xl font-bold text-indigo-600">
            SONGZUG - Social Network
          </div>
        </div>
      </header>

      {/* Main Content Section */}
      <main className="flex-grow flex items-center justify-center p-8">
        <div className="max-w-7xl w-full flex flex-col md:flex-row items-center justify-between gap-12">
          {/* Left Side: Illustration and Core Message */}
          <div className="md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left">
            <h1 className="text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
              Connect. Share. Discover.
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Join the new generation of social networking.
            </p>

            {/* Illustration Placeholder (Left side of the login box) */}
            <div className="w-full max-w-md h-64 bg-indigo-100 rounded-lg shadow-xl flex items-center justify-center border-4 border-indigo-200">
              {/* Optional: Add an actual image/SVG here later */}
              <img
                src="https://i.pinimg.com/736x/7d/42/53/7d4253205fe987a1b26d884b4b07ec83.jpg"
                alt="Social Media Illustration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right Side: Login/Sign Up Box */}
          <div className="md:w-1/3 w-full max-w-sm bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
              Start Connecting
            </h2>
            {message}
            <form className="space-y-4" onSubmit={submitLogin}>
              {/* Email/Username Input */}
              <div>
                <label htmlFor="email" className="sr-only">
                  Email or Username
                </label>
                <input
                  id="email"
                  name="email"
                  type="text"
                  value={inputValues.email}
                  onChange={(e) =>
                    setInputValues({ ...inputValues, email: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500 text-gray-900"
                  placeholder="Email or Username"
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={inputValues.password}
                  onChange={(e) =>
                    setInputValues({ ...inputValues, password: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500 text-gray-900"
                  placeholder="Password"
                />
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150"
              >
                Log In
              </button>
            </form>

            {/* Separator and Sign Up */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 mb-4">
                Don't have an account?
              </p>
              <Link
                to="/registration"
                type="button"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-indigo-600 bg-indigo-100 hover:bg-indigo-200 transition duration-150"
              >
                Create New Account
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Section */}
      <footer className="py-6 px-8 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <div className="space-x-4">
            <a href="#" className="hover:text-indigo-600 transition">
              Privacy Policy
            </a>
            <span className="text-gray-300">|</span>
            <a href="#" className="hover:text-indigo-600 transition">
              Terms of Service
            </a>
            <span className="text-gray-300">|</span>
            <a href="#" className="hover:text-indigo-600 transition">
              Contact
            </a>
            <span className="text-gray-300">|</span>
            <a href="#" className="hover:text-indigo-600 transition">
              Help Center
            </a>
          </div>
          <p className="mt-3">
            &copy; {new Date().getFullYear()} Songzug. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
